import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Button from '../../../components/ui/Button';


const GiftingAnalyticsCard = ({ analytics, onViewDetails }) => {
  const monthlyData = [
    { month: 'Jan', amount: 450 },
    { month: 'Feb', amount: 320 },
    { month: 'Mar', amount: 680 },
    { month: 'Apr', amount: 290 },
    { month: 'May', amount: 540 },
    { month: 'Jun', amount: 720 }
  ];

  const categoryData = [
    { name: 'Electronics', value: 35, color: '#FF6B6B' },
    { name: 'Fashion', value: 25, color: '#4ECDC4' },
    { name: 'Books', value: 20, color: '#45B7D1' },
    { name: 'Home', value: 15, color: '#96CEB4' },
    { name: 'Other', value: 5, color: '#FFEAA7' }
  ];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-soft border border-accent-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
            <Icon name="BarChart3" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-accent-800">Gift Analytics</h3>
            <p className="text-sm text-accent-600">Your gifting insights</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={onViewDetails}
          className="text-primary hover:text-primary-700"
        >
          View Details
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-surface rounded-xl">
          <div className="text-2xl font-bold text-accent-800">${analytics.totalSpent}</div>
          <div className="text-xs text-accent-600">Total Spent</div>
        </div>
        <div className="text-center p-4 bg-surface rounded-xl">
          <div className="text-2xl font-bold text-accent-800">{analytics.giftsGiven}</div>
          <div className="text-xs text-accent-600">Gifts Given</div>
        </div>
        <div className="text-center p-4 bg-surface rounded-xl">
          <div className="text-2xl font-bold text-success-600">{analytics.successRate}%</div>
          <div className="text-xs text-accent-600">Success Rate</div>
        </div>
        <div className="text-center p-4 bg-surface rounded-xl">
          <div className="text-2xl font-bold text-primary">{analytics.avgRating}</div>
          <div className="text-xs text-accent-600">Avg Rating</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Spending */}
        <div>
          <h4 className="font-medium text-accent-800 mb-3">Monthly Spending</h4>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
                <Bar dataKey="amount" fill="#FF6B6B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution */}
        <div>
          <h4 className="font-medium text-accent-800 mb-3">Gift Categories</h4>
          <div className="flex items-center gap-4">
            <div className="h-32 w-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={20}
                    outerRadius={50}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {categoryData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-accent-600 flex-1">{item.name}</span>
                  <span className="text-sm font-medium text-accent-800">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Achievements */}
      <div className="mt-6 pt-6 border-t border-accent-100">
        <h4 className="font-medium text-accent-800 mb-3">Recent Achievements</h4>
        <div className="flex flex-wrap gap-2">
          {(analytics.achievements || []).map((achievement, index) => (
            <div key={index} className="flex items-center gap-2 px-3 py-2 bg-success-50 text-success-700 rounded-full text-sm">
              <Icon name="Award" size={14} />
              {achievement}
            </div>
          ))}
          {(!analytics.achievements || analytics.achievements.length === 0) && (
            <p className="text-sm text-accent-500 italic">No achievements yet. Keep gifting to unlock rewards!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftingAnalyticsCard;